/**
 * ProductDetailOverlay
 *
 * Self-contained product detail screen used inside the Guided Flow when the
 * user opens a product card. Handles options (grouped + ungrouped),
 * supplements, allergen pills, validation of required selections, price
 * recalculation and the "Añadir" CTA.
 *
 * Extracted from GuidedFlow.js to keep the main state machine focused.
 */
import React, { useState } from 'react';
import { ChevronLeft, AlertCircle, Plus, Check } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { categoryImages } from '../../hooks/useCategoryData';
import { optimizedImageUrl, lazyImgProps } from '../../lib/imageOptim';
import { tr } from '../../lib/guidedFlowI18n';
import { T, S } from './utils';

const ProductDetailOverlay = ({ product, onClose, onAdd, localize }) => {
  const { language: lang } = useLanguage();
  const language = lang || 'es';
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [validationError, setValidationError] = useState(null);

  if (!product) return null;

  const loc = (v) => localize(v);
  const hasSupplements = product.supplements?.length > 0;

  // Group options by their `group` property; ungrouped fall into a separate
  // bucket and render as pills instead of radio-style cards.
  const getGroupedOptions = (options) => {
    const grouped = {};
    const ungrouped = [];
    (options || []).forEach(opt => {
      if (opt.group) {
        if (!grouped[opt.group]) grouped[opt.group] = [];
        grouped[opt.group].push(opt);
      } else {
        ungrouped.push(opt);
      }
    });
    return { grouped, ungrouped };
  };

  const groupTitleKey = {
    tipo_pan: 'grp_tipo_pan',
    tipo_vino: 'grp_tipo_vino',
    tipo_leche: 'grp_tipo_leche',
    tipo_bebida: 'grp_tipo_bebida',
    tipo_bikini: 'grp_tipo_bikini',
    ensalada: 'grp_ensalada',
    principal: 'grp_principal',
    postre: 'grp_postre',
  };

  const toggleOption = (option) => {
    setSelectedOptions(prev => {
      const optName = loc(option.name);
      const exists = prev.find(o => loc(o.name) === optName);
      if (option.group) {
        if (exists) return prev.filter(o => loc(o.name) !== optName);
        return [...prev.filter(o => o.group !== option.group), option];
      }
      if (exists) return prev.filter(o => loc(o.name) !== optName);
      return [...prev, option];
    });
  };

  const isSelected = (option) => selectedOptions.some(o => loc(o.name) === loc(option.name));

  const calculateTotal = () => {
    const extra = selectedOptions.reduce((s, o) => s + (o.price_modifier || 0), 0);
    return (product.price || 0) + extra;
  };

  const handleAdd = () => {
    if (product.requires_selection) {
      const { grouped } = getGroupedOptions(product.options);
      const missing = Object.keys(grouped).filter(g => !selectedOptions.some(o => o.group === g));
      if (missing.length > 0) {
        setValidationError(tr(language, 'validation_required'));
        return;
      }
    }
    if (product.requires_bread_selection && !selectedOptions.some(o => o.group === 'tipo_pan')) {
      setValidationError(tr(language, 'validation_bread'));
      return;
    }
    if (product.requires_option_selection && !selectedOptions.some(o => o.group === 'tipo_vino')) {
      setValidationError(tr(language, 'validation_wine'));
      return;
    }
    setValidationError(null);
    onAdd(product, selectedOptions);
  };

  const { grouped, ungrouped } = getGroupedOptions(product.options);
  const groupKeys = Object.keys(grouped);
  const imgSrc = product.image_url || product.images?.[0] || categoryImages['cat-picar'];

  return (
    <div data-testid="product-detail-overlay" style={{
      position: 'absolute', inset: 0, zIndex: 50, background: T.bg,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', padding: '16px 20px', position: 'relative', zIndex: 2,
        background: T.bg,
      }}>
        <button onClick={onClose} data-testid="detail-close-btn" style={{
          background: 'none', border: 'none', color: T.gold, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: '4px', fontFamily: T.font.b, fontSize: '15px',
        }}>
          <ChevronLeft size={20} /> {tr(language, 'back')}
        </button>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {/* Product image */}
        <div style={{ width: '100%', height: '240px', position: 'relative' }}>
          <img src={optimizedImageUrl(imgSrc, 700)} alt={loc(product.name)} draggable={false}
            {...lazyImgProps}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: '80px',
            background: `linear-gradient(transparent, ${T.bg})`,
          }} />
        </div>

        <div style={{ padding: '0 24px 120px' }}>
          {/* Name + price */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <h2 style={{
              fontFamily: T.font.h, fontSize: '26px', fontWeight: 500, color: T.text,
              lineHeight: 1.2, margin: 0, flex: 1,
            }}>
              {loc(product.name)}
            </h2>
            <span style={{
              fontFamily: T.font.h, fontSize: '24px', fontWeight: 600, color: T.gold,
              flexShrink: 0, marginLeft: '12px',
            }}>
              {calculateTotal().toFixed(2)}€
            </span>
          </div>

          {/* Description */}
          {product.description && loc(product.description) && (
            <p style={{
              fontFamily: T.font.b, fontSize: '15px', color: T.textSec,
              lineHeight: 1.6, marginBottom: '24px',
            }}>
              {loc(product.description)}
            </p>
          )}

          {/* Grouped options (single select per group) */}
          {groupKeys.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <p style={{
                fontFamily: T.font.b, fontSize: '12px', fontWeight: 600, letterSpacing: '0.12em',
                textTransform: 'uppercase', color: T.gold, marginBottom: '16px',
              }}>
                {tr(language, 'customize_choice')}
              </p>
              {groupKeys.map(group => (
                <div key={group} style={{ marginBottom: '16px' }}>
                  <p style={{
                    fontFamily: T.font.b, fontSize: '14px', fontWeight: 500, color: T.textSec,
                    marginBottom: '10px',
                  }}>
                    {groupTitleKey[group] ? tr(language, groupTitleKey[group]) : group}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {grouped[group].map((opt, idx) => {
                      const sel = isSelected(opt);
                      const name = loc(opt.name).replace(/^(Ensalada|Principal|Postre):\s*/i, '');
                      return (
                        <button key={idx} onClick={() => toggleOption(opt)}
                          data-testid={`detail-option-${group}-${idx}`}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '12px', width: '100%',
                            padding: '14px 16px', borderRadius: '14px', cursor: 'pointer',
                            border: sel ? `2px solid ${T.gold}` : `1px solid ${T.border}`,
                            background: sel ? '#2A2622' : T.bgEl, textAlign: 'left',
                            transition: 'all 0.15s',
                          }}
                        >
                          <div style={{
                            width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: sel ? T.gold : T.border,
                          }}>
                            {sel && <Check size={12} color={T.bg} />}
                          </div>
                          <span style={{
                            flex: 1, fontFamily: T.font.b, fontSize: '15px',
                            fontWeight: sel ? 600 : 400, color: T.text,
                          }}>
                            {name}
                          </span>
                          {opt.price_modifier !== 0 && opt.price_modifier != null && (
                            <span style={{ fontFamily: T.font.b, fontSize: '14px', color: T.gold }}>
                              {opt.price_modifier > 0 ? '+' : ''}{opt.price_modifier.toFixed(2)}€
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Ungrouped options */}
          {ungrouped.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <p style={{
                fontFamily: T.font.b, fontSize: '12px', fontWeight: 600, letterSpacing: '0.12em',
                textTransform: 'uppercase', color: T.gold, marginBottom: '12px',
              }}>
                {tr(language, 'options_label')}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {ungrouped.map((opt, idx) => {
                  const sel = isSelected(opt);
                  return (
                    <button key={idx} onClick={() => toggleOption(opt)}
                      data-testid={`detail-option-${idx}`}
                      style={{
                        padding: '10px 16px', borderRadius: '999px', cursor: 'pointer',
                        border: sel ? `2px solid ${T.gold}` : `1px solid ${T.border}`,
                        background: sel ? '#2A2622' : T.bgEl,
                        fontFamily: T.font.b, fontSize: '14px', color: T.text,
                        transition: 'all 0.15s',
                      }}
                    >
                      {loc(opt.name)}
                      {opt.price_modifier !== 0 && opt.price_modifier != null && (
                        <span style={{ marginLeft: '6px', color: T.gold }}>
                          {opt.price_modifier > 0 ? '+' : ''}{opt.price_modifier.toFixed(2)}€
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Supplements */}
          {hasSupplements && (
            <div style={{ marginBottom: '20px' }}>
              <p style={{
                fontFamily: T.font.b, fontSize: '12px', fontWeight: 600, letterSpacing: '0.12em',
                textTransform: 'uppercase', color: T.gold, marginBottom: '12px',
              }}>
                {tr(language, 'extras_label')}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {product.supplements.map((supp, idx) => {
                  const sel = isSelected(supp);
                  return (
                    <button key={idx} onClick={() => toggleOption(supp)}
                      data-testid={`detail-supplement-${idx}`}
                      style={{
                        padding: '10px 16px', borderRadius: '999px', cursor: 'pointer',
                        border: sel ? `2px solid ${T.gold}` : `1px solid ${T.border}`,
                        background: sel ? '#2A2622' : T.bgEl,
                        fontFamily: T.font.b, fontSize: '14px', color: T.text,
                        transition: 'all 0.15s',
                      }}
                    >
                      {loc(supp.name)}
                      {supp.price_modifier > 0 && (
                        <span style={{ marginLeft: '6px', color: T.gold }}>
                          +{supp.price_modifier.toFixed(2)}€
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Allergens */}
          {product.allergens && product.allergens.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <p style={{
                fontFamily: T.font.b, fontSize: '12px', fontWeight: 600, letterSpacing: '0.12em',
                textTransform: 'uppercase', color: T.gold, marginBottom: '8px',
              }}>
                {tr(language, 'allergens_label')}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {(Array.isArray(product.allergens) ? product.allergens : Object.keys(product.allergens)).map(a => (
                  <span key={a} style={{
                    padding: '6px 12px', borderRadius: '999px', fontSize: '12px',
                    background: `${T.gold}15`, border: `1px solid ${T.gold}30`,
                    color: T.textSec, fontFamily: T.font.b,
                  }}>
                    {a}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Validation error */}
          {validationError && (
            <div data-testid="detail-validation-error" style={{
              display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px',
              borderRadius: '12px', background: 'rgba(220,80,80,0.12)',
              border: '1px solid rgba(220,80,80,0.25)', marginBottom: '16px',
            }}>
              <AlertCircle size={18} color="#DC5050" />
              <p style={{ fontFamily: T.font.b, fontSize: '14px', color: '#DC5050', margin: 0 }}>
                {validationError}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Fixed bottom CTA */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, padding: '16px 24px 32px',
        background: `linear-gradient(transparent, ${T.bg} 30%)`,
      }}>
        <button onClick={handleAdd} data-testid="detail-add-btn" style={{
          ...S.goldBtn,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        }}>
          <Plus size={18} /> {tr(language, 'add_btn')} &middot; {calculateTotal().toFixed(2)}&euro;
        </button>
      </div>
    </div>
  );
};

export default ProductDetailOverlay;
